import React from "react";
import { StyledButton, StyledSwipeableDrawer, StyledTypography } from "../../../../../styled-mui";
import MobileMenuWrapper from "../MobileMenuWrapper";
import { Box } from "@mui/material";
import { ImageLayout } from "../../../../elements/image/ImageLayout";
import SmartLink from "../../../../elements/smart-link/smart-link";
import { useNavigate } from "react-router";
import AsyncCall from "../../../../../_foundation/gtm/async.service";
import storeUtil from "../../../../../utils/storeUtil";
import { CLICK } from "../../../../../_foundation/constants/gtm";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

interface Props {
  open: boolean;
  entireMenuCloseHandler: () => any;
  closeCurrentMenu: () => any;
  menuLinksData: Record<string, any>;
  pageParams?: Record<string, any> | null;
  categories?: Record<string, any>[];
  pathname?: string;
}

const MobileOutilsMenu: React.FC<Props> = ({
  open,
  entireMenuCloseHandler,
  closeCurrentMenu,
  menuLinksData,
  pageParams = null,
  categories = [],
  pathname = "",
}) => {
  const navigate = useNavigate();
  const OutilsData = menuLinksData?.outils;
  const universData = OutilsData?.Univers || [];
  const NAV_LEVEL_1 = "Outils De Conception";

  const onOutilsBtnClick = () => {
    AsyncCall.sendClick(CLICK.NAV, {
      click_section: "menu",
      nav_level_1: NAV_LEVEL_1,
      nav_level_2: "",
      nav_level_3: "",
      nav_type: "cta",
      btn_label: "Voir tous nos configurateurs",
      click_destination: OutilsData?.url || "",
    });
    entireMenuCloseHandler();
    navigate(OutilsData?.url);
  };

  const onLinkClick = (item: Record<string, any>, universe: Record<string, any>) => {
    AsyncCall.sendClick(CLICK.NAV, {
      click_section: "menu",
      nav_level_1: NAV_LEVEL_1,
      nav_level_2: universe?.name || "",
      nav_level_3: item?.title || "",
      nav_type: "link",
      btn_label: item?.title || "",
      click_destination: item?.link?.url,
    });
    entireMenuCloseHandler();
  };

  const isMagazineItem = (itemLabelOrUrl: string, obj?: Record<string, any>) => {
    if (!itemLabelOrUrl) return false;
    const label = (itemLabelOrUrl || "").toString().toLowerCase();
    const url = (obj?.link?.url || "").toString().toLowerCase();
    if (menuLinksData?.magazine && menuLinksData.magazine.label) {
      return label === menuLinksData.magazine.label.toString().toLowerCase();
    }
    return url.includes("magazine") || label.includes("magazine");
  };

  const shouldObfuscate = (item: Record<string, any>) => {
    if (!item) return false;
    const pageType = pageParams?.page?.type || "";
    // Product pages: obfuscate everything
    if (typeof pageType === "string" && pageType.toLowerCase().includes("product")) return true;
    // Category pages: obfuscate magazine and non-sister/top-level links
    if (typeof pageType === "string" && pageType.toLowerCase().includes("category")) {
      if (isMagazineItem(item?.title || item?.link?.url || "", item)) return true;
      const currentIdentifier = pageParams?.identifier || pageParams?.tokenValue || null;
      const currentParent = pageParams?.parentCategoryId || null;
      if (!currentParent) {
        const itemIdentifier = item?.identifier || item?.title || "";
        const isTopLevel = categories?.some((c) => c?.identifier === itemIdentifier);
        return !isTopLevel;
      }
      const itemIdentifier2 = item?.identifier || item?.title || "";
      const isSisterOrCurrent =
        itemIdentifier2 === currentIdentifier || categories?.some((c) => c?.identifier === itemIdentifier2);
      return !isSisterOrCurrent;
    }
    return false;
  };

  return (
    <StyledSwipeableDrawer
      anchor="left"
      open={open}
      onClose={closeCurrentMenu}
      onOpen={() => null}
      className="header-menu-level2 mobile-outils-menu">
      <MobileMenuWrapper
        title="Outils de conception"
        isASubMenu
        entireMenuCloseHandler={entireMenuCloseHandler}
        closeCurrentMenu={closeCurrentMenu}>
        <Box p={3}>
          {universData?.map((item: Record<string, any>, index: number) => (
            <Box key={item?.name + index} className="configurateur-mobile-entry" mb={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                {item?.icon && (
                  <ImageLayout
                    src={storeUtil.getUrlPicture(`${item.icon}`, "")}
                    height={24}
                    width={24}
                    styles={{ margin: "unset" }}
                  />
                )}
                <StyledTypography
                  variant="body1"
                  component="p"
                  style={{ fontSize: "15px", fontWeight: "600", lineHeight: "20px" }}>
                  {item?.name}
                </StyledTypography>
              </Box>
              <Box className="configurateur-mobile-links">
                {item?.configurateurs?.map((config: Record<string, any>, index: number) => {
                  const obfuscate = shouldObfuscate(config);
                  return obfuscate ? (
                    <span
                      key={config?.title + index}
                      role="link"
                      tabIndex={0}
                      style={{ display: "block", padding: "12px 0px", fontWeight: 500 }}
                      onClick={() => {
                        onLinkClick(config, item);
                        const url = config?.link?.url;
                        const target = config?.link?.type === "external" ? "_blank" : config?.link?.target;
                        if (target === "_blank") window.open(url, "_blank");
                        else if (url) navigate(url);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          onLinkClick(config, item);
                          const url = config?.link?.url;
                          const target = config?.link?.type === "external" ? "_blank" : config?.link?.target;
                          if (target === "_blank") window.open(url, "_blank");
                          else if (url) navigate(url);
                        }
                      }}>
                      {config?.title}
                    </span>
                  ) : (
                    <SmartLink
                      key={config?.title + index}
                      isExternal={config?.link?.type === "external"}
                      to={config?.link?.url || ""}
                      onClick={() => onLinkClick(config, item)}
                      styles={{
                        display: "block",
                        padding: "12px 0px",
                        fontWeight: "500",
                        fontSize: "16px",
                        lineHeight: "100%",
                        color: "rgba(66, 66, 66, 1)",
                      }}>
                      {config?.title}
                    </SmartLink>
                  );
                })}
              </Box>
            </Box>
          ))}

          <StyledButton
            variant="text"
            onClick={onOutilsBtnClick}
            sx={{
              display: "flex",
              alignItems: "center",
              columnGap: "8px",
              fontSize: "15px",
              fontWeight: "500",
              lineHeight: "15px",
              textTransform: "none",
              color: "rgba(93, 137, 162, 1)",
              marginBottom: "35px",
            }}>
            Voir tous nos configurateurs
            <ArrowForwardIcon sx={{ width: 20 }} />
          </StyledButton>
        </Box>
      </MobileMenuWrapper>
    </StyledSwipeableDrawer>
  );
};

export default MobileOutilsMenu;
